import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { defaultRange, rangeParams, useDebounced, useLoad, when } from "@/lib/staff";
import { INPUT, PageHead, Pager, RangePicker, Select, Table } from "@/components/staff/ui";
import { KindBadge } from "@/components/staff/bits";

const KINDS = [["", "All kinds"], ["server", "Server errors"], ["ai", "Failed AI requests"], ["job", "Failed background jobs"]];

const COLUMNS = [
  { key: "created_at", label: "When", render: (e) => <span className="whitespace-nowrap text-xs">{when(e.created_at)}</span> },
  { key: "kind", label: "Kind", render: (e) => <KindBadge kind={e.kind} /> },
  { key: "area", label: "Area", render: (e) => <span className="font-mono text-xs break-all">{e.area}</span> },
  { key: "error_type", label: "Type", render: (e) => <span className="text-xs">{e.error_type}</span> },
  { key: "message", label: "Message", className: "max-w-[420px]", render: (e) => <span className="text-xs text-neutral-300 line-clamp-3" title={e.message}>{e.message}</span> },
  { key: "student", label: "Student", render: (e) => (e.user_id ? <Link to={`/staff/students/${e.user_id}`} className="text-xs text-sky-300 hover:underline" data-testid={`error-student-${e.error_id}`}>{e.user_email || "Open student"}</Link> : <span className="text-xs text-neutral-600">—</span>) },
  { key: "details", label: "Details", right: true, render: (e) => <span className="text-xs text-neutral-400">{[e.model, e.ms != null && `${(e.ms / 1000).toFixed(1)} s`, e.status].filter(Boolean).join(" · ") || "—"}</span> },
];

export default function ErrorsPage() {
  const [sp, setSp] = useSearchParams();
  const userId = sp.get("user_id") || "";
  const [range, setRange] = useState(() => defaultRange("7d"));
  const [kind, setKind] = useState("");
  const [text, setText] = useState("");
  const q = useDebounced(text.trim());
  const [page, setPage] = useState(1);
  const params = useMemo(() => ({ ...rangeParams(range), kind, q, user_id: userId, page }), [range, kind, q, userId, page]);
  const { data, error, loading, reload } = useLoad("/staff/errors", params);
  const reset = (fn) => (v) => { fn(v); setPage(1); };
  return (
    <div data-testid="staff-errors">
      <PageHead title="Errors" sub="Server errors, failed AI requests and failed background jobs. Messages are shortened and secrets are removed; prompts and answers are never stored." />
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="errors-filters">
        <RangePicker value={range} onChange={reset(setRange)} testid="errors-range" />
        <Select value={kind} onChange={reset(setKind)} options={KINDS} label="Kind" testid="errors-kind-filter" />
        <input value={text} onChange={(e) => { setText(e.target.value); setPage(1); }} placeholder="Search message, area, type or model" className={`${INPUT} w-72`} aria-label="Search errors" data-testid="errors-search-input" />
        {userId && (
          <button onClick={() => { setSp({}, { replace: true }); setPage(1); }} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-sky-400/15 text-sky-100 text-xs" data-testid="errors-student-filter-clear">
            One student only <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <Table testid="errors-table" columns={COLUMNS} rows={data?.items} rowKey={(e) => e.error_id} loading={loading} error={error} onRetry={reload} empty="No errors in this range." />
      {data && <Pager page={page} pageSize={data.page_size} total={data.total} onPage={setPage} testid="errors-pager" />}
    </div>
  );
}
